import { supabase } from "./supabase.js";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";

// Matches /api/create-payment + /api/confirm-payment on the server.
// Pricing is re-validated server-side so client can't tamper with amounts.
export const PLAN_PRICE = {
  once: 49000,    // legacy 1회 분석 (현재 UI는 패키지로 대체)
  pkg1: 120000,
  pkg4: 390000,
  pkg8: 690000,
  pkg12: 890000,
  basic: 34000,   // 구독은 사용 안 함 (서버가 거부)
  pro: 58000,
};

export const PLAN_CREDITS = {
  pkg1: 1,
  pkg4: 4,
  pkg8: 8,
  pkg12: 12,
};

export const PLAN_LABEL = {
  once: "1회 영상 분석 리포트",
  pkg1: "i-talk 분석권 1회",
  pkg4: "i-talk 분석권 4회",
  pkg8: "i-talk 분석권 8회",
  pkg12: "i-talk 분석권 12회",
};

// 50% 할인 이벤트: total/perCase는 할인가, original은 할인 전 정가(취소선 표시용).
export const PACKAGE_OPTIONS = [
  { key: "pkg1",  credits: 1,  total: 60000,  perCase: 60000, original: 120000, discountPct: 50 },
  { key: "pkg4",  credits: 4,  total: 195000, perCase: 48750, original: 390000, discountPct: 50, badge: "추천" },
  { key: "pkg8",  credits: 8,  total: 345000, perCase: 43125, original: 690000, discountPct: 50 },
  { key: "pkg12", credits: 12, total: 445000, perCase: 37083, original: 890000, discountPct: 50, badge: "최고가성비" },
];

let tossCache = null;
export async function getTossPayments() {
  if (tossCache) return tossCache;
  const clientKey = import.meta.env.VITE_TOSS_CLIENT_KEY;
  if (!clientKey) {
    throw new Error("VITE_TOSS_CLIENT_KEY 환경변수가 없어요 (Vercel 환경변수 확인)");
  }
  tossCache = await loadTossPayments(clientKey);
  return tossCache;
}

async function authedFetch(path, body) {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error("로그인이 필요해요");

  const res = await fetch(path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(b.error ?? `HTTP ${res.status}`);
  }
  return await res.json();
}

// Server inserts a pending it_purchases row and returns { orderId, amount, orderName, customerKey }.
// caseId는 legacy 'once' 결제에서만 필수. 패키지(pkg1/3/5/10)는 case와 무관하게 user 풀에 적립.
export function createPaymentIntent({ caseId, planType }) {
  const body = { plan_type: planType };
  if (caseId) body.case_id = caseId;
  return authedFetch("/api/create-payment", body);
}

// 사용자의 잔여 분석권 합계 (만료 제외). RPC: it_user_credit_balance(p_user_id).
export async function fetchCreditBalance(userId) {
  if (!userId) return 0;
  const { data, error } = await supabase.rpc("it_user_credit_balance", {
    p_user_id: userId,
  });
  if (error) throw error;
  return Number(data ?? 0);
}

// FIFO로 1 credit 차감. 차감된 package_id 반환 (잔여 0이면 null).
// 차감 후 case를 draft → received로 전이. 동시성으로 차감만 되고 case 전이가
// 실패하면 NO_CREDIT가 아닌 일반 에러로 throw.
export async function consumeCreditAndSubmit({ userId, caseId }) {
  if (!userId || !caseId) throw new Error("userId/caseId 필요");
  const { data: pkgId, error: rpcErr } = await supabase.rpc("it_consume_credit", {
    p_user_id: userId,
  });
  if (rpcErr) throw rpcErr;
  if (!pkgId) {
    const err = new Error("NO_CREDIT");
    err.code = "NO_CREDIT";
    throw err;
  }
  const { data, error } = await supabase
    .from("it_cases")
    .update({
      status: "received",
      submitted_at: new Date().toISOString(),
    })
    .eq("id", caseId)
    .eq("status", "draft")
    .select("id, status, submitted_at")
    .maybeSingle();
  if (error) throw error;
  if (!data) {
    // 케이스가 이미 draft가 아님 — credit은 차감됐지만 전이는 0건.
    // 짱샘 운영 환경에선 거의 발생 안 함, 발생 시 운영자가 수동 보정.
    throw new Error("케이스가 이미 제출되었거나 상태가 변경됐어요");
  }
  return { caseId: data.id, packageId: pkgId };
}

// Server calls Toss /v1/payments/confirm, flips purchase → paid and case → received.
export function confirmPayment({ orderId, paymentKey, amount }) {
  return authedFetch("/api/confirm-payment", {
    order_id: orderId,
    payment_key: paymentKey,
    amount,
  });
}

// 본인이 받은 분석권 패키지 + 연결된 결제 내역. 무료권(purchase_id=null)도 포함.
// 정렬: 최신순(구매일 = paid_at 우선, 없으면 created_at).
export async function listMyPurchases(userId) {
  if (!userId) return [];
  const { data, error } = await supabase
    .from("it_credit_packages")
    .select(`
      id, package_type, credits_total, credits_remaining,
      created_at, expires_at,
      purchase:it_purchases(
        id, amount_won, plan_type, status,
        payment_provider, payment_ref, method,
        paid_at, refunded_at, refund_reason, created_at
      )
    `)
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => {
    const purchase = Array.isArray(row.purchase) ? row.purchase[0] : row.purchase;
    return { ...row, purchase: purchase ?? null };
  });
}

// Toss 영수증 URL을 lazy로 받아옴. 새 탭에서 열 때 직전에 호출.
export async function fetchReceiptUrl(paymentKey) {
  if (!paymentKey) throw new Error("paymentKey 필요");
  const { url } = await authedFetch("/api/payment-receipt", {
    payment_key: paymentKey,
  });
  return url;
}
